<script setup lang="ts">
import {computed} from "vue";
import {Circle, CircleCheck, Trash2} from "@lucide/vue";
import MedicationCheckupPosologyLine from "@/medication-checkup/components/MedicationCheckupPosologyLine.vue";
import MedicationCheckupDispensingsTag from "@/medication-checkup/components/MedicationCheckupDispensingsTag.vue";
import {TreatmentLine} from "@/medication-checkup/interfaces/treatment-selections";
import {useTreatmentSelectionsStore} from "@/medication-checkup/stores/treatment-selections-store";

/**
 * A line of the collection that no prescription carries — a counter
 * purchase, or a product added during the interview — as a tile: checked,
 * dosed from the dialog, and, when it was added by hand, deleted.
 */

const props = defineProps<{
    line: TreatmentLine;
    /**
     * Whether the line can be deleted: only a hand-added one can, a scanned
     * one is dropped by leaving it unchecked.
     */
    removable?: boolean;
}>();

const emit = defineEmits<{
    edit: [];
    remove: [];
}>();

const selection = useTreatmentSelectionsStore();

const checked = computed(() => selection.isChecked(props.line));

/**
 * Where a hand-added product comes from, said under its name.
 */
const detail = computed(() => {
    if (props.line.collected) {
        return null;
    }

    return props.line.source === 'prescription' ? 'Ajout · sur ordonnance' : 'Ajout · hors ordonnance';
});
</script>

<template>
  <div
    class="flex cursor-pointer flex-col gap-1.5 rounded-[10px] border border-solid px-3 py-2.5 transition-colors"
    :class="checked ? 'border-secondary-darkest/40 bg-secondary-lightest/30' : 'border-slate-200 bg-white'"
    @click="selection.toggle(line)"
  >
    <span class="flex items-start gap-2.5">
      <span class="mt-px shrink-0" :title="checked ? 'Retirer du bilan' : 'Retenir pour le bilan'">
        <CircleCheck v-if="checked" class="size-4 text-secondary-darkest" />
        <Circle v-else class="size-4 text-gray" />
      </span>
      <span class="flex min-w-0 flex-1 flex-col">
        <span class="text-[11.5px] font-semibold text-gray-dark">{{ line.label }}</span>
        <span v-if="detail" class="text-[10.5px] text-muted-foreground">{{ detail }}</span>
      </span>
      <button
        v-if="removable"
        type="button"
        class="shrink-0 text-slate-400 hover:text-red-light"
        title="Supprimer cet ajout"
        @click.stop="emit('remove')"
      >
        <Trash2 class="size-4" />
      </button>
    </span>
    <MedicationCheckupPosologyLine
      :posology="selection.posology(line)"
      editable
      :asked="checked"
      @edit="emit('edit')"
    />
    <MedicationCheckupDispensingsTag v-if="line.history" :history="line.history" />
  </div>
</template>
